import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import apiClient from '../api/client';
import wjCoverImage from '../assets/images/wj-cover.jpg';
import LoadingSpinner from '../components/common/LoadingSpinner';
import ErrorDisplay from '../components/common/ErrorDisplay';
import PageTransitionWrapper from '../components/layout/PageTransitionWrapper';

// --- CUSTOM HOOK DEBOUNCE ---
function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => clearTimeout(handler);
    }, [value, delay]);

    return debouncedValue;
}

const WartaJemaat = () => {
    // --- STATES ---
    const [wartaList, setWartaList] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);
    const [error, setError] = useState(null);
    const [progress, setProgress] = useState(0);

    const [searchTerm, setSearchTerm] = useState(''); // State input instan
    const [filterYear, setFilterYear] = useState('');
    const [filterMonth, setFilterMonth] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const itemsPerPage = 6;

    // --- DEBOUNCE LOGIC ---
    const debouncedSearchTerm = useDebounce(searchTerm, 500);

    // Animasi Loading Internal saat Debounce bekerja
    // Jika searchTerm berbeda dengan debouncedSearchTerm, berarti user sedang mengetik
    const isSearching = searchTerm !== debouncedSearchTerm;

    // --- 1. FETCH SEMUA DATA ---
    useEffect(() => {
        let isCancelled = false;
        const fetchAllPages = async () => {
            setIsLoading(true);
            setProgress(10);
            try {
                const firstPage = await apiClient.getWartaJemaatList(1);
                let combinedData = [...firstPage.wartaList];
                const total = firstPage.totalPages;

                if (total > 1) {
                    const promises = [];
                    for (let i = 2; i <= total; i++) {
                        promises.push(apiClient.getWartaJemaatList(i));
                    }
                    const remainingPages = await Promise.all(promises);
                    remainingPages.forEach(page => {
                        combinedData = [...combinedData, ...page.wartaList];
                    });
                }

                if (!isCancelled) {
                    const sortedData = combinedData.sort((a, b) =>
                        new Date(b.created_at) - new Date(a.created_at)
                    );
                    setWartaList(sortedData);
                    setProgress(100);
                }
            } catch (err) {
                if (!isCancelled) setError('Gagal memuat arsip warta.');
            } finally {
                if (!isCancelled) setTimeout(() => setIsLoading(false), 500);
            }
        };

        fetchAllPages();
        return () => { isCancelled = true; };
    }, []);

    // --- 2. LOGIKA TAHUN OTOMATIS ---
    const years = useMemo(() => {
        const allYears = wartaList.map(item => {
            const date = new Date(item.created_at);
            return !isNaN(date.getTime()) ? date.getFullYear().toString() : null;
        });
        return [...new Set(allYears)].filter(y => y !== null).sort((a, b) => b - a);
    }, [wartaList]);

    const months = [
        { val: '01', name: 'Januari' }, { val: '02', name: 'Februari' },
        { val: '03', name: 'Maret' }, { val: '04', name: 'April' },
        { val: '05', name: 'Mei' }, { val: '06', name: 'Juni' },
        { val: '07', name: 'Juli' }, { val: '08', name: 'Agustus' },
        { val: '09', name: 'September' }, { val: '10', name: 'Oktober' },
        { val: '11', name: 'November' }, { val: '12', name: 'Desember' }
    ];

    // --- 3. LOGIKA FILTER (MENGGUNAKAN DEBOUNCE) ---
    const filteredResults = useMemo(() => {
        return wartaList.filter((item) => {
            const searchLower = debouncedSearchTerm.toLowerCase(); // Pakai debouncedSearchTerm
            const date = new Date(item.created_at);
            const isValidDate = !isNaN(date.getTime());

            const itemYear = isValidDate ? date.getFullYear().toString() : "";
            const itemMonth = isValidDate ? (date.getMonth() + 1).toString().padStart(2, '0') : "";
            const itemTitle = item.title ? item.title.toLowerCase() : "";

            const matchesSearch = itemTitle.includes(searchLower) || itemYear.includes(searchLower);
            const matchesYear = filterYear ? itemYear === filterYear : true;
            const matchesMonth = filterMonth ? itemMonth === filterMonth : true;

            return matchesSearch && matchesYear && matchesMonth;
        });
    }, [wartaList, debouncedSearchTerm, filterYear, filterMonth]);

    // --- 4. PAGINATION LOGIC ---
    const totalPages = Math.ceil(filteredResults.length / itemsPerPage);

    const paginationRange = useMemo(() => {
        const delta = 1;
        const range = [];
        const rangeWithDots = [];
        let l;
        for (let i = 1; i <= totalPages; i++) {
            if (i === 1 || i === totalPages || (i >= currentPage - delta && i <= currentPage + delta)) {
                range.push(i);
            }
        }
        for (let i of range) {
            if (l) {
                if (i - l === 2) rangeWithDots.push(l + 1);
                else if (i - l !== 1) rangeWithDots.push('...');
            }
            rangeWithDots.push(i);
            l = i;
        }
        return rangeWithDots;
    }, [currentPage, totalPages]);

    const currentData = useMemo(() => {
        const start = (currentPage - 1) * itemsPerPage;
        return filteredResults.slice(start, start + itemsPerPage);
    }, [filteredResults, currentPage]);

    // --- RESET KE HALAMAN 1 SAAT PENCARIAN BERUBAH ---
    useEffect(() => {
        setCurrentPage(1);
    }, [debouncedSearchTerm, filterYear, filterMonth]);

    // --- 5. HANDLERS ---
    const handleFilterUpdate = (type, value) => {
        if (type === 'search') {
            setSearchTerm(value);
        } else {
            setIsFiltering(true);
            if (type === 'year') setFilterYear(value);
            if (type === 'month') setFilterMonth(value);
            setTimeout(() => setIsFiltering(false), 400); // Durasi loading halus
        }
    };

    const handleResetFilter = () => {
        setIsFiltering(true);
        setSearchTerm('');
        setFilterYear('');
        setFilterMonth('');
        setCurrentPage(1);
        setTimeout(() => setIsFiltering(false), 400);
    };

    if (error) return <div className="min-h-screen flex items-center justify-center p-4"><ErrorDisplay message={error} /></div>;

    return (
        <div className="bg-gray-50 min-h-screen py-12 px-4 sm:px-6 lg:px-8">
            <div className="max-w-4xl mx-auto">
                <div className="bg-white rounded-xl shadow-2xl p-6 sm:p-8 relative overflow-hidden border-b-8 border-blue-600 mb-8">
                    <div className="absolute top-0 right-0 h-full w-1/4 bg-blue-50 opacity-50 skew-x-12 transform origin-top-right hidden sm:block"></div>
                    <h1 className="text-4xl sm:text-5xl font-black text-gray-900 text-center relative z-10">WARTA JEMAAT</h1>
                    <p className="text-center text-gray-500 mt-2 text-lg relative z-10 font-medium text-uppercase">Arsip Informasi dan Jadwal Pelayanan</p>
                </div>

                <div className="bg-white p-5 rounded-2xl shadow-sm mb-8 border border-gray-100">
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-center">
                        <div className="sm:col-span-2 relative">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">🔍</span>
                            <input
                                type="text"
                                placeholder="Cari judul..."
                                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none transition-all text-sm"
                                value={searchTerm}
                                onChange={(e) => handleFilterUpdate('search', e.target.value)}

                            />
                            {isSearching && (
                                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                                    <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                </div>
                            )}
                        </div>
                        <select
                            className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer text-sm font-bold text-gray-600"
                            value={filterYear}
                            onChange={(e) => handleFilterUpdate('year', e.target.value)}
                        >
                            <option value="">Semua Tahun</option>
                            {years.map(y => <option key={y} value={y}>{y}</option>)}
                        </select>
                        <select
                            className="w-full p-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 outline-none cursor-pointer text-sm font-bold text-gray-600"
                            value={filterMonth}
                            onChange={(e) => handleFilterUpdate('month', e.target.value)}
                        >
                            <option value="">Semua Bulan</option>
                            {months.map(m => <option key={m.val} value={m.val}>{m.name}</option>)}
                        </select>
                    </div>

                    {(searchTerm || filterYear || filterMonth) && (
                        <div className="mt-4 pt-4 border-t border-gray-50 flex justify-center">
                            <button
                                onClick={handleResetFilter}
                                className="flex items-center gap-2 text-red-500 hover:text-red-700 font-bold text-xs uppercase tracking-widest transition-colors"
                            >
                                <span>✕</span> Hapus Filter
                            </button>
                        </div>
                    )}
                </div>

                <div className="mt-8 bg-white rounded-xl shadow-lg p-6 sm:p-8 min-h-[500px] relative">
                    {(isLoading || isFiltering) ? (
                        <div className="absolute inset-0 z-30 flex justify-center items-center bg-white/60 backdrop-blur-sm rounded-xl">
                            <LoadingSpinner progress={isLoading ? progress : 100} />
                        </div>
                    ) : (
                        <PageTransitionWrapper>
                            <div
                                className={`transition-all duration-300 ease-in-out ${isSearching
                                    ? 'opacity-40 blur-[1px] scale-[0.98] pointer-events-none'
                                    : 'opacity-100 blur-0 scale-100'
                                    }`}
                            >
                                {currentData.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center py-16 px-4 bg-white rounded-3xl border border-gray-100 shadow-xl shadow-gray-50/50">
                                        <div className="relative mb-8">
                                            <div className="absolute inset-0 bg-blue-50 rounded-full scale-150 opacity-60 animate-pulse"></div>
                                            <div className="relative bg-gradient-to-br from-white to-gray-50 w-24 h-24 rounded-2xl shadow-sm border border-white flex items-center justify-center">
                                                <span className="text-5xl transform -rotate-12">🔎</span>
                                            </div>
                                        </div>
                                        <div className="text-center max-w-sm">
                                            <h3 className="text-2xl font-black text-gray-900 mb-3 tracking-tight">
                                                {searchTerm || filterYear || filterMonth ? "Hasil Tidak Ditemukan" : "Belum Ada Data"}
                                            </h3>
                                            <p className="text-gray-500 font-medium leading-relaxed">
                                                {searchTerm || filterYear || filterMonth
                                                    ? `Kami tidak menemukan apa pun untuk "${searchTerm}". Coba gunakan kata kunci lain.`
                                                    : "Saat ini belum ada informasi yang tersedia."}
                                            </p>
                                        </div>
                                        {(searchTerm || filterYear || filterMonth) && (
                                            <button
                                                onClick={handleResetFilter}
                                                className="mt-10 flex items-center gap-3 bg-blue-600 hover:bg-blue-700 text-white px-8 py-3.5 rounded-2xl font-bold transition-all shadow-lg active:scale-95"
                                            >
                                                🔄 Atur Ulang Pencarian
                                            </button>
                                        )}
                                    </div>
                                ) : (
                                    <>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                            {currentData.map((warta) => (
                                                <WartaCard key={warta.id} warta={warta} />
                                            ))}
                                        </div>

                                        {totalPages > 1 && (
                                            <div className="mt-12 flex justify-center items-center gap-2">
                                                <button
                                                    onClick={() => { setCurrentPage(prev => Math.max(prev - 1, 1)); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                    disabled={currentPage === 1}
                                                    className="h-10 w-10 flex items-center justify-center rounded-lg border bg-white disabled:opacity-30 hover:bg-gray-100 transition-all"
                                                >‹</button>
                                                {paginationRange.map((page, i) => (
                                                    <button
                                                        key={i}
                                                        onClick={() => { if (page !== '...') { setCurrentPage(page); window.scrollTo({ top: 300, behavior: 'smooth' }); } }}
                                                        className={`h-10 min-w-[40px] rounded-lg font-bold transition-all ${page === currentPage ? 'bg-blue-600 text-white shadow-lg' : 'bg-white text-gray-500 border hover:bg-gray-100'}`}
                                                    >
                                                        {page}
                                                    </button>
                                                ))}
                                                <button
                                                    onClick={() => { setCurrentPage(prev => Math.min(prev + 1, totalPages)); window.scrollTo({ top: 300, behavior: 'smooth' }); }}
                                                    disabled={currentPage === totalPages}
                                                    className="h-10 w-10 flex items-center justify-center rounded-lg border bg-white disabled:opacity-30 hover:bg-gray-100 transition-all"
                                                >›</button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </div>
                        </PageTransitionWrapper>
                    )}
                </div>
            </div>
        </div>
    );
};

const WartaCard = ({ warta }) => (
    <Link
        to={`/warta-jemaat/${warta.id}`}
        className="bg-white rounded-2xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-2xl transform hover:-translate-y-2 transition-all duration-300 flex flex-col group"
    >
        <div className="relative h-48 overflow-hidden bg-gray-100">
            <div className="absolute top-4 right-4 z-10">
                <span className="bg-blue-600 text-white text-[11px] font-black px-4 py-1.5 rounded-full uppercase tracking-tighter shadow-md">
                    Warta Jemaat
                </span>
            </div>
            <div
                className="w-full h-full bg-cover bg-center transition-transform duration-700 group-hover:scale-110"
                style={{ backgroundImage: `url(${warta.image_url || wjCoverImage})` }}
            />
            <div className="absolute inset-0 bg-black/5 group-hover:bg-transparent transition-colors" />
        </div>

        <div className="p-5 flex text-center flex-col justify-between flex-grow">
            <div className="mb-2">
                <h3 className="text-2xl font-black text-gray-900 group-hover:text-blue-700 transition-colors uppercase leading-[1.2] tracking-tight line-clamp-2">
                    {warta.title}
                </h3>
                <div
                    className="text-gray-400 text-sm mt-3 line-clamp-2 italic font-medium leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: warta.desc }}
                />
            </div>

            <div className="mt-4 pt-5 border-t border-gray-100">
                <p className="text-sm font-black text-blue-500 flex items-center justify-center tracking-widest uppercase">
                    <span className="mr-2 text-lg">📅</span>
                    {new Date(warta.created_at).toLocaleDateString('id-ID', {
                        day: 'numeric', month: 'long', year: 'numeric'
                    })}
                </p>
            </div>
        </div>
    </Link>
);

export default WartaJemaat;